import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import Validator from 'validatorjs';
import { withRouter, Link } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Modal,
  Checkbox,
  Dropdown,
  Message,
  Loader,
  Dimmer,
} from 'semantic-ui-react';

import { isEmpty, get } from 'lodash';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import auctionLocationsQuery from '../../graphql/auctionLocationsQuery';
import vraReasonsQuery from '../../graphql/vraReasonsQuery';
import { TITLE_STATUS_OPTIONS } from '../../constants';
import client from '../../apollo';
import 'react-datepicker/dist/react-datepicker.css';
import { utcMoment, dateTimeFormatted } from '../../utilities/dateUtils';
import returnInvoiceQuery from '../../graphql/returnInvoiceQuery';

const FIXED_GROUP_NAME_LIST = [
  {
    id: 1,
    groupName: 'High Recon Costs',
  },
  {
    id: 2,
    groupName: 'Denied Arbitration',
  },
  {
    id: 3,
    groupName: 'Mechanical/Structural Issue',
  },
  {
    id: 4,
    groupName: 'Additional Reasons',
  },
];

function matchingGroupName(returnReasonItem) {
  const found = FIXED_GROUP_NAME_LIST.find(
    fixedGroupItem => fixedGroupItem.id === returnReasonItem.groupId
  );
  if (isEmpty(found)) {
    return 'Other Groups';
  }
  return found.groupName;
}

function mappingCheckboxesReturnReasonsIds(selectedReasons) {
  const states = {};
  selectedReasons.forEach(id => {
    states[id] = true;
  });
  return states;
}

class ReturnInvoiceEdit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      id: null,
      returnReasons: [],
      returnReasonsCheckbox: {},
      pendingArbitration: false,
      auctionLocationId: '',
      odometerOnReturn: '',
      mechanicalIssues: false,
      cosmeticIssues: false,
      unableToSell: false,
      changedMind: false,
      other: false,
      noAnswer: false,
      estimatedCost: '',
      refundProcessed: false,
      titleNotificationSent: false,
      titleReceived: false,
      vehicleReceived: false,
      expirationDate: '',
      titleStatus: '',
      userEmail: '',
      loading: true,
      error: null,
      message: null,
      errors: {},
      locations: [],
    };
  }

  UNSAFE_componentWillMount() {
    const { returnInvoice } = this.props;
    this.locationQuery();
    this.fetchAllReturnReasons();
    if (!isEmpty(returnInvoice)) {
      const {
        id,
        expirationDate,
        estimatedCost,
        userEmail,
        mechanicalIssues,
        cosmeticIssues,
        unableToSell,
        changedMind,
        other,
        noAnswer,
        additionalInformation,
        auctionLocationId,
        odometerOnReturn,
        titleStatus,
        transaction: {
          refundProcessed,
          returnLocation,
          vehicleReceived,
          titleReceived,
        },
      } = returnInvoice;

      this.fetchReturnInvoiceById(id);

      this.setState({
        id: parseInt(id, 10),
        expirationDate: moment(expirationDate),
        odometerOnReturn,
        refundProcessed: refundProcessed || false,
        returnLocation: returnLocation.initials,
        vehicleReceived: vehicleReceived || false,
        titleReceived: titleReceived || false,
        estimatedCost,
        userEmail,
        mechanicalIssues,
        cosmeticIssues,
        unableToSell,
        changedMind,
        other,
        noAnswer,
        additionalInformation,
        auctionLocationId: parseInt(auctionLocationId, 10),
        titleStatus,
      });
    }
  }

  locationQuery = () => {
    client
      .query({
        query: auctionLocationsQuery,
        variables: {
          sortBy: 'initials',
          acceptsReturns: true,
        },
      })
      .then(({ data }) => {
        this.setState({
          locations: get(data, 'locations'),
        });
      })
      .catch(() => {
        this.setState({ locations: [] });
      });
  };

  fetchAllReturnReasons = () => {
    client
      .query({
        query: vraReasonsQuery,
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (isEmpty(data)) {
          this.setState({
            errors: {},
            error: 'Fetch VRA Reasons unsuccessfully.',
            message: null,
          });
          return;
        }

        // Map group names to reasons
        const mappedReturnReasonsData = get(data, 'vraReasons')
          .filter(item => item.active)
          .sort((reason1, reason2) => reason1.orderId - reason2.orderId)
          .map(item => ({
            ...item,
            groupName: matchingGroupName(item),
          }));

        this.setState({
          returnReasons: mappedReturnReasonsData,
        });
      })
      .catch(() => {
        this.setState({
          errors: {},
          error: 'Send the VRA Reasons Update unsuccessfully.',
          message: null,
        });
      });
  };

  fetchReturnInvoiceById = id => {
    client
      .query({
        query: returnInvoiceQuery,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (isEmpty(data)) {
          this.setState({
            errors: {},
            error: 'Fetch VRA Reason Unsuccessfully.',
            message: null,
          });
          return;
        }
        const { pendingArbitration, selectedReasons } = data.returnInvoice;

        const selectedStates = mappingCheckboxesReturnReasonsIds(selectedReasons);

        this.setState({
          ...selectedStates,
          pendingArbitration,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({
          errors: {},
          error: 'Fetch VRA Reason failed (Network Error).',
          message: null,
        });
      });
  };

  // Function to get the list of checkboxes and their states
  getReturnReasonsIdsCheckboxes = (excludes = []) => {
    const { returnReasons } = this.state;
    const selectedIds = [];
    returnReasons.forEach(item => {
      const isItemChecked = this.state[item.id] || false; // eslint-disable-line react/destructuring-assignment

      if (!excludes.includes(item.returnReason) && isItemChecked) {
        selectedIds.push(item.id);
      }
    });
    return selectedIds;
  };

  // Function to get the list of checkboxes and their states
  getReturnReasonsCheckboxStates = (excludes = []) => {
    const { returnReasons } = this.state;
    const checkboxStates = {};
    returnReasons.forEach(item => {
      if (!excludes.includes(item.returnReason)) {
        checkboxStates[item.returnReason] = this.state[item.id] || false; // eslint-disable-line react/destructuring-assignment
      }
    });
    return checkboxStates;
  };

  getReasonsSelectedExceptOtherValidation = (includes = []) => {
    // list selected
    const selectedReturnReasonsExceptOther = this.getReturnReasonsCheckboxStates([
      'Other',
    ]);
    const selectedReturnReasonsExceptOtherValues = Object.values(
      selectedReturnReasonsExceptOther
    );

    const returnReasonsCheckboxExceptOtherValidation = [
      ...includes,
      ...selectedReturnReasonsExceptOtherValues,
    ];

    return returnReasonsCheckboxExceptOtherValidation.some(reason => reason);
  };

  clearMessages() {
    this.setState({
      errors: {},
      error: null,
      message: null,
    });
  }

  verifyFormFields(fields = {}) {
    const validationRules = {
      pendingArbitration: 'boolean',
      additionalInformation: 'string|max:1000',
    };
    const validation = new Validator(
      isEmpty(fields) ? this.state : fields,
      validationRules
    );
    this.clearMessages();

    if (validation.fails()) {
      this.setState({
        ...validation.errors,
      });
      return false;
    }
    return true;
  }

  submitForm = () => {
    const { onSubmitHandler } = this.props;
    const {
      id,
      pendingArbitration,
      expirationDate,
      odometerOnReturn,
      refundProcessed,
      auctionLocationId,
      vehicleReceived,
      titleReceived,
      estimatedCost,
      userEmail,
      mechanicalIssues,
      cosmeticIssues,
      unableToSell,
      changedMind,
      noAnswer,
      additionalInformation,
      titleNotificationSent,
      titleStatus,
    } = this.state;

    // Validation
    const selectedReturnReasons = this.getReturnReasonsCheckboxStates();
    const other = selectedReturnReasons.Other;

    const reasonsSelectedExceptOther = this.getReasonsSelectedExceptOtherValidation();

    if (!this.verifyFormFields()) {
      return false;
    } if (!reasonsSelectedExceptOther && !other) {
      this.setState({
        errors: {},
        error: 'Please select at least one return reason.',
        message: null,
      });
      return false;
    } if (
      !reasonsSelectedExceptOther
      && other
      && isEmpty(additionalInformation)
    ) {
      this.setState({
        errors: {},
        error: 'Please input the additional information.',
        message: null,
      });
      return false;
    }
    const selectedReasons = this.getReturnReasonsIdsCheckboxes();
    const payload = {
      id: parseInt(id, 10),
      pendingArbitration,
      selectedReasons,
      expirationDate: dateTimeFormatted(utcMoment(expirationDate)),
      odometerOnReturn: parseInt(odometerOnReturn, 10),
      refundProcessed,
      auctionLocationId,
      vehicleReceived,
      titleReceived,
      estimatedCost,
      userEmail,
      mechanicalIssues,
      cosmeticIssues,
      unableToSell,
      changedMind,
      other,
      noAnswer,
      additionalInformation,
      titleNotificationSent,
      titleStatus,
    };
    onSubmitHandler(payload);

    this.setState({ errors: {}, error: null, message: 'Form Submitted.' });
    return true;
  };

  render() {
    const {
      isEditModalOpen, onCloseHandler, submitting,
    } = this.props;
    const {
      locations,
      loading,
      errors,
      pendingArbitration,
      additionalInformation,
      estimatedCost,
      odometerOnReturn,
      auctionLocationId,
      returnLocation,
      titleStatus,
      userEmail,
      expirationDate,
      vehicleReceived,
      titleReceived,
      refundProcessed,
      titleNotificationSent,
      returnReasons,
      error,
      message,
    } = this.state;

    const onChangeHandler = field => event => {
      if (field === 'additionalInformation') {
        this.verifyFormFields({ [field]: event.target.value });
        this.setState({ [field]: event.target.value });
        return;
      }
      if (field !== 'estimatedCost' && field !== 'odometerOnReturn') {
        this.setState({ [field]: event.target.value });
      } else if (event.target.value !== '') {
        this.setState({ [field]: parseFloat(event.target.value) });
      } else {
        this.setState({ [field]: 0 });
      }
    };

    const onCheckboxHandler = field => (event, data) => {
      this.setState({ [field]: data.checked });
    };

    const onSelectHandler = field => (event, data) => {
      this.setState({ [field]: data.value });
    };

    const date = isEmpty(expirationDate)
      ? null
      : utcMoment(expirationDate);

    const onDateChangeHandler = field => dateValue => {
      this.setState({ [field]: moment(dateValue, 'YYYY-MM-DD HH:mm:ss') });
    };

    const returnLocationOptions = !isEmpty(locations)
      && locations.map(location => {
        return {
          text: `${location.initials} - ${location.name}`,
          key: location.id,
          value: parseInt(location.id, 10),
        };
      });

    // Group the data by groupId
    const groupedData = returnReasons.reduce((acc, item) => {
      const { groupId } = item;
      if (!acc[groupId]) {
        acc[groupId] = [];
      }
      acc[groupId].push(item);
      return acc;
    }, {});

    const findGroupNameByGroupId = groupId => {
      const foundItem = FIXED_GROUP_NAME_LIST.find(
        group => group.id === groupId
      );
      if (isEmpty(foundItem)) {
        return 'Other groups';
      }
      return foundItem.groupName;
    };

    return (
      <Modal
        open={isEditModalOpen}
        closeIcon
        onClose={onCloseHandler}
        closeOnEscape={false}
        closeOnRootNodeClick={false}
      >
        <Modal.Header>Edit Reasons For Return</Modal.Header>
        <Modal.Content image>
          <Modal.Description>
            {loading && (
              <Dimmer active inverted className='dimmer-center'>
                <Loader active inline='centered' size='large'>
                  Loading...
                </Loader>
              </Dimmer>
            )}
            {!loading && (
              <div>
                {error && (
                  <Message negative>
                    <p>{error}</p>
                  </Message>
                )}
                {message && (
                  <Message positive>
                    <p>{message}</p>
                  </Message>
                )}
                <Form>
                  <Form.Field>
                    <Checkbox
                      label='Pending Arb'
                      checked={pendingArbitration}
                      onChange={onCheckboxHandler('pendingArbitration')}
                    />
                    {errors.pendingArbitration && (
                      <span style={{ color: 'red' }}>
                        {errors.pendingArbitration}
                      </span>
                    )}
                  </Form.Field>
                  <div>
                    {Object.keys(groupedData).map(groupId => (
                      <div key={groupId}>
                        <h4>{findGroupNameByGroupId(groupId)}</h4>
                        <Form.Group>
                          {groupedData[groupId].map(item => (
                            <Form.Field key={item.id}>
                              <Checkbox
                                label={item.returnReason}
                                checked={this.state[item.id] || false} // eslint-disable-line react/destructuring-assignment
                                onChange={onCheckboxHandler(item.id)}
                              />{' '}
                            </Form.Field>
                          ))}
                        </Form.Group>
                      </div>
                    ))}
                  </div>
                  <Form.Field>
                    <label htmlFor='additionalInformation'>Additional Information:</label>
                    <Input
                      id='additionalInformation'
                      name='additionalInformation'
                      value={additionalInformation}
                      onChange={onChangeHandler('additionalInformation')}
                    />
                    {errors.additionalInformation && (
                      <span style={{ color: 'red' }}>
                        {errors.additionalInformation}
                      </span>
                    )}
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor='estimatedCost'>Estimated cost to correct above issue(s):</label>
                    <Input
                      id='estimatedCost'
                      name='estimatedCost'
                      value={estimatedCost}
                      onChange={onChangeHandler('estimatedCost')}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor='odometerOnReturn'>Odometer Reading(miles):</label>
                    <Input
                      id='odometerOnReturn'
                      name='odometerOnReturn'
                      value={odometerOnReturn}
                      onChange={onChangeHandler('odometerOnReturn')}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor='auctionLocationId'>Return Location:</label>
                    {!isEmpty(locations) && (
                      <Dropdown
                        id='auctionLocationId'
                        options={returnLocationOptions}
                        value={auctionLocationId}
                        selected={returnLocation}
                        onChange={onSelectHandler('auctionLocationId')}
                        fluid
                        selection
                        selectOnBlur={false}
                      />
                    )}
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor='titleStatus'>Title Status:</label>
                    <Dropdown
                      id='titleStatus'
                      options={TITLE_STATUS_OPTIONS}
                      value={titleStatus}
                      selected={titleStatus}
                      onChange={onSelectHandler('titleStatus')}
                      fluid
                      selection
                      selectOnBlur={false}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor='userEmail'>User Email:</label>
                    <Input
                      id='userEmail'
                      name='userEmail'
                      value={userEmail}
                      onChange={onChangeHandler('userEmail')}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor='expirationDate'>Return Expiration Date:</label>
                    <DatePicker
                      id='expirationDate'
                      style={{ padding: '0' }}
                      selected={date}
                      onChange={onDateChangeHandler('expirationDate')}
                      showTimeSelect
                      timeFormat='HH:mm'
                      timeIntervals={15}
                      dateFormat='YYYY-MM-DD hh:mm:ss ZZ'
                    />
                  </Form.Field>
                  <Form.Group widths='1'>
                    <Form.Field>
                      <Checkbox
                        label='Vehicle Received:'
                        checked={vehicleReceived}
                        onChange={onCheckboxHandler('vehicleReceived')}
                      />{' '}
                      {'  '}
                      <Checkbox
                        label='Title Received:'
                        checked={titleReceived}
                        onChange={onCheckboxHandler('titleReceived')}
                      />{' '}
                      {'  '}
                      <Checkbox
                        label='Refund Processed:'
                        checked={refundProcessed}
                        onChange={onCheckboxHandler('refundProcessed')}
                      />{' '}
                      {'  '}
                    </Form.Field>
                  </Form.Group>
                  <Form.Group widths='1'>
                    <Form.Field>
                      <Link to=''>New Audit</Link> {'  '}
                      <Link to=''>Send Title Notice</Link>
                      {'  '}
                      <Link to=''>Send Return Notice</Link> {'  '}
                    </Form.Field>
                  </Form.Group>
                  <Form.Group widths='1'>
                    <Form.Field>
                      <Checkbox
                        label='Send an email with the updates?'
                        checked={titleNotificationSent}
                        onChange={onCheckboxHandler('titleNotificationSent')}
                      />{' '}
                      {'  '}
                    </Form.Field>
                  </Form.Group>
                </Form>
              </div>
            )}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button color='green' loading={submitting} disabled={submitting} onClick={this.submitForm}>
            Update
          </Button>
          <Button onClick={onCloseHandler} disabled={submitting}>Cancel</Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

ReturnInvoiceEdit.propTypes = {
  returnInvoice: PropTypes.shape({
    id: PropTypes.string,
    expirationDate: PropTypes.string,
    estimatedCost: PropTypes.number,
    userEmail: PropTypes.string,
    mechanicalIssues: PropTypes.bool,
    cosmeticIssues: PropTypes.bool,
    unableToSell: PropTypes.bool,
    changedMind: PropTypes.bool,
    other: PropTypes.bool,
    noAnswer: PropTypes.bool,
    additionalInformation: PropTypes.string,
    auctionLocationId: PropTypes.string,
    odometerOnReturn: PropTypes.number,
    titleStatus: PropTypes.string,
    transaction: PropTypes.shape({
      refundProcessed: PropTypes.bool,
      returnLocation: PropTypes.shape({
        initials: PropTypes.string,
      }),
      vehicleReceived: PropTypes.bool,
      titleReceived: PropTypes.bool,
    }),
  }),
  isEditModalOpen: PropTypes.bool,
  onCloseHandler: PropTypes.func,
  onSubmitHandler: PropTypes.func,
  submitting: PropTypes.bool,
};

export default compose(withRouter)(ReturnInvoiceEdit);
